/**
 * "Maybe later" at any stop: play what you have (Tom, 2026-10-01, ruling
 * r-2026-10-01-maybe-later-at-any-paywall-stop).
 *
 *   "No one who taps maybe later should have progressed wiped. They just go
 *    into play what you have and their progress stays at their last new LEGO.
 *    They can still go back and forth using the cursor belt skip and all
 *    top/bottom nav arrows."
 *
 * Two pure decisions the player wires in:
 *  - at a round boundary, what to do when the next round is past the wall:
 *    the wall the first time; once the learner has said "Maybe later", keep
 *    playing what they have instead of walling them again every round;
 *  - where that play continues: back to the start of the belt they are on,
 *    inside what they can play, never into locked rounds.
 * Their progress is held by paywallRetreat meanwhile (no position write while
 * held), so recycling never moves the place they reopen the app on.
 */
export type PaywallBoundaryStep = 'continue' | 'wall' | 'recycle'

export function paywallBoundaryStep(
  nextSeed: number | null,
  canAccessSeed: (seed: number) => boolean,
  playingWhatTheyHave: boolean,
): PaywallBoundaryStep {
  if (nextSeed === null || canAccessSeed(nextSeed)) return 'continue'
  return playingWhatTheyHave ? 'recycle' : 'wall'
}

interface RoundLike { legoId?: string | null; seedId?: string | null }

function seedOf(r: RoundLike | null | undefined): number | null {
  const id = r?.seedId || r?.legoId?.slice(0, 5)
  const m = id ? /^S(\d{4})/.exec(id) : null
  return m ? Number(m[1]) : null
}

/**
 * The round play-what-you-have continues from: the first playable round at or
 * after the current belt's start (when that is behind the learner), else the
 * first playable round in the queue. Null when nothing at all is playable.
 */
export function recycleRound(
  rounds: ReadonlyArray<RoundLike | null | undefined>,
  fromIndex: number,
  canAccessSeed: (seed: number) => boolean,
  beltStartIndex: number,
): number | null {
  const playable = (i: number) => {
    const s = seedOf(rounds[i])
    return s === null || canAccessSeed(s)
  }
  const start = beltStartIndex >= 0 && beltStartIndex < fromIndex ? beltStartIndex : 0
  for (let i = start; i <= Math.min(fromIndex, rounds.length - 1); i++) if (playable(i)) return i
  for (let i = 0; i < rounds.length; i++) if (playable(i)) return i
  return null
}
